import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarClock, Loader2, Users, Play, Save } from "lucide-react";

export default function WeeklyMarketResearch() {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [method, setMethod] = useState("scraper");
  const [limit, setLimit] = useState(2000);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/market/weekly-research/run?dry_run=true");
      setStatus(data);
      setMethod(data.method || "scraper");
      setLimit(data.limit || 2000);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Durum alınamadı"); }
    finally { setLoading(false); }
  };

  const onOpenChange = (v) => { setOpen(v); if (v) load(); };

  const saveConfig = async () => {
    try {
      await api.put("/market/weekly-research/config", { method, limit: Number(limit) });
      toast.success("Ayar kaydedildi");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Kaydedilemedi"); }
  };

  const runNow = async (force = false) => {
    setRunning(true);
    try {
      const { data } = await api.post(`/market/weekly-research/run?dry_run=false&force=${force}`);
      toast.success(`Tarama başlatıldı — ${data.products_total} ürün (${data.method})`);
      setTimeout(load, 800);
    } catch (e) {
      const msg = apiError(e.response?.data?.detail) || "Başlatılamadı";
      toast.error(msg);
    } finally { setRunning(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button data-testid="weekly-market-btn" variant="outline" className="border-slate-300">
          <CalendarClock className="mr-1.5 h-4 w-4" /> Haftalık Piyasa Taraması
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="weekly-market-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarClock className="h-5 w-5 text-brand-green" /> Haftalık Piyasa Öğrenme</DialogTitle>
          <DialogDescription>
            Her Pazar (İstanbul) gece taramasında, <b>aktif kullanıcı 0</b> iken tüm katalog için piyasa fiyatı araştırılır.
            Buradan durumu görebilir ve elle "Şimdi Kontrol Et" yapabilirsiniz.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
              <span className="flex items-center gap-2 text-sm text-slate-600"><Users className="h-4 w-4" /> Çevrimiçi kullanıcı (son 10 dk)</span>
              <span data-testid="weekly-active-users" className={`rounded-full px-2.5 py-0.5 text-sm font-bold ${status?.quiescent ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                {status?.active_users ?? "—"} {status?.quiescent ? "· sakin" : "· meşgul"}
              </span>
            </div>

            <div className="rounded-lg border border-slate-200 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Ayarlar</p>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <label className="text-xs text-slate-600">Yöntem
                  <select data-testid="weekly-method" value={method} onChange={(e) => setMethod(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm">
                    <option value="scraper">Site Tarama (ücretsiz)</option>
                    <option value="ai">Google AI (güvenilir, ücretli)</option>
                  </select>
                </label>
                <label className="text-xs text-slate-600">Ürün üst sınırı
                  <input data-testid="weekly-limit" type="number" min="1" max="5000" value={limit}
                    onChange={(e) => setLimit(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
                </label>
              </div>
              <Button data-testid="weekly-save-config" size="sm" variant="outline" onClick={saveConfig} className="mt-2">
                <Save className="mr-1.5 h-3.5 w-3.5" /> Ayarı Kaydet
              </Button>
            </div>

            {status?.products_total != null && (
              <p className="text-xs text-slate-500">Katalogda <b>{status.products_total}</b> aktif ürün. Bu tarama en fazla <b>{limit}</b> ürünü işler.</p>
            )}

            <div className="flex flex-wrap gap-2">
              <Button data-testid="weekly-run-now" onClick={() => runNow(false)} disabled={running}
                className="bg-brand-green text-white hover:bg-brand-green-600">
                {running ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Play className="mr-1.5 h-4 w-4" />} Şimdi Tara
              </Button>
              {!status?.quiescent && (
                <Button data-testid="weekly-run-force" onClick={() => runNow(true)} disabled={running} variant="outline" className="border-amber-300 text-amber-700">
                  Yine de Başlat (zorla)
                </Button>
              )}
            </div>
            {status?.last_run && (
              <p className="text-[11px] text-slate-400">Son çalışma: {(status.last_run.finished_at || status.last_run.started_at || status.last_run.at || "").slice(0, 19).replace("T", " ")}
                {status.last_run.ok != null ? ` · ${status.last_run.ok}/${status.last_run.total} kaynaklı` : ""}
                {status.last_run.skipped ? " · ertelendi (aktif kullanıcı)" : ""}</p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
